import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardBody, CardHeader, Modal, Row, Col, Alert, Spinner, Input, Button } from 'reactstrap';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../helpers/api';
import FeatherIcon from 'feather-icons-react';
import * as XLSX from 'xlsx';
import { formatCreatedDate } from '../constants/utils';
import PermissionGuard from '../components/Common/PermissionGuard';

const TagGenerator = () => {
    document.title = "Tag Generator | Arcmedix";

    // History state
    const [batches, setBatches] = useState([]);
    const [loading, setLoading] = useState(false);
    const [count, setCount] = useState(10);
    const [hasMore, setHasMore] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [apiError, setApiError] = useState('');

    // Generate modal state
    const [generateModal, setGenerateModal] = useState(false);
    const [tagCount, setTagCount] = useState(100);
    const [description, setDescription] = useState('');
    const [generating, setGenerating] = useState(false);
    const [generationResult, setGenerationResult] = useState(null);
    const [previewUuid, setPreviewUuid] = useState('');

    // Export modal state
    const [exportModal, setExportModal] = useState(false);
    const [selectedBatch, setSelectedBatch] = useState(null);
    const [exportFormat, setExportFormat] = useState('xlsx');
    const [exporting, setExporting] = useState(false);

    const PORTAL_URL = 'https://portal.arcmedix.com/assets';

    // Generate preview hex tag ID when modal opens
    const generateHexId = () =>
        Array.from(crypto.getRandomValues(new Uint8Array(8)))
            .map(b => b.toString(16).padStart(2, '0'))
            .join('')
            .toUpperCase();

    useEffect(() => {
        if (generateModal) {
            setPreviewUuid(generateHexId());
        }
    }, [generateModal]);

    const fetchHistory = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(
                `tag/history?count=${count}${searchTerm ? `&search=${searchTerm}` : ''}`
            );
            setBatches(data.response || []);
            setHasMore((data.response || []).length >= count);
        } catch (error) {
            console.error('Error fetching tag history:', error);
            setHasMore(false);
        } finally {
            setLoading(false);
        }
    }, [count, searchTerm]);

    const loadMoreHistory = async () => {
        if (!hasMore || loading) return;

        setLoading(true);
        try {
            const newCount = count + 10;
            const { data } = await api.get(
                `tag/history?count=${newCount}${searchTerm ? `&search=${searchTerm}` : ''}`
            );
            setBatches(data.response || []);
            setCount(newCount);
            setHasMore((data.response || []).length >= newCount);
        } catch (error) {
            console.error('Error loading more history:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        const term = e.target.value;
        setSearchTerm(term);
        setCount(10);
        setHasMore(true);
    };

    const handleGenerateTags = async () => {
        if (tagCount < 1 || tagCount > 10000) {
            setApiError('Please enter a number between 1 and 10,000');
            return;
        }

        setGenerating(true);
        setApiError('');
        try {
            const { data } = await api.post('tag/generate', {
                count: tagCount,
                description: description.trim() || undefined
            });
            setGenerationResult(data.response);
            await fetchHistory();
        } catch (error) {
            setApiError(error?.response?.data?.error || 'Failed to generate tags');
        } finally {
            setGenerating(false);
        }
    };

    const handleExport = async () => {
        if (!selectedBatch) return;

        setExporting(true);
        try {
            const { data } = await api.get(`tag/batch/${selectedBatch.batchId}`);
            const tags = data.response?.tags || [];

            const dataToExport = tags.map((tag, index) => ({
                '#': index + 1,
                'UUID': tag.uuid,
                'Full URL': `${PORTAL_URL}/${tag.uuid}`,
                'Generated Date': formatCreatedDate(tag.createdAt)
            }));

            const ws = XLSX.utils.json_to_sheet(dataToExport);
            const dateStr = new Date().toISOString().split('T')[0];

            if (exportFormat === 'xlsx') {
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, 'Tags');
                XLSX.writeFile(wb, `tags_${selectedBatch.batchId}_${dateStr}.xlsx`);
            } else {
                const csv = XLSX.utils.sheet_to_csv(ws);
                const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = `tags_${selectedBatch.batchId}_${dateStr}.csv`;
                link.click();
                URL.revokeObjectURL(link.href);
            }

            setExportModal(false);
        } catch (error) {
            setApiError(error?.response?.data?.error || 'Failed to export tags');
        } finally {
            setExporting(false);
        }
    };

    const handleExportGenerated = (format = 'xlsx') => {
        if (!generationResult?.tags) return;

        const dataToExport = generationResult.tags.map((tag, index) => ({
            '#': index + 1,
            'UUID': tag.uuid,
            'Full URL': `${PORTAL_URL}/${tag.uuid}`,
            'Generated Date': formatCreatedDate(tag.createdAt)
        }));

        const ws = XLSX.utils.json_to_sheet(dataToExport);
        const dateStr = new Date().toISOString().split('T')[0];

        if (format === 'xlsx') {
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Tags');
            XLSX.writeFile(wb, `generated_tags_${dateStr}.xlsx`);
        } else {
            const csv = XLSX.utils.sheet_to_csv(ws);
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `generated_tags_${dateStr}.csv`;
            link.click();
            URL.revokeObjectURL(link.href);
        }
    };

    const resetGenerateModal = () => {
        setGenerateModal(false);
        setTagCount(100);
        setDescription('');
        setGenerationResult(null);
        setApiError('');
    };

    const openExportModal = (batch) => {
        setSelectedBatch(batch);
        setExportFormat('xlsx');
        setExportModal(true);
    };

    useEffect(() => {
        fetchHistory();
    }, [fetchHistory]);

    return (
        <div className='page-content mt-4'>
            {/* Header */}
            <div className="mb-4 d-flex align-items-center gap-3">
                <motion.div
                    className="d-flex align-items-center gap-3 w-100"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    <div style={{ minWidth: '300px', flexGrow: 1, maxWidth: '400px' }}>
                        <Input
                            type="text"
                            placeholder="Search batches..."
                            value={searchTerm}
                            onChange={handleSearch}
                        />
                    </div>
                    <PermissionGuard permission="tag:generate">
                        <Button
                            color="success"
                            className="btn-soft-success d-flex align-items-center gap-2"
                            onClick={() => setGenerateModal(true)}
                        >
                            <FeatherIcon icon="plus" size={18} />
                            Generate Tags
                        </Button>
                    </PermissionGuard>
                </motion.div>
            </div>

            {/* Main Card */}
            <Card>
                <CardHeader>
                    <div className="d-flex justify-content-between align-items-center">
                        <h3 className="card-title text-success mb-0">Tag Generation History</h3>
                    </div>
                </CardHeader>

                <CardBody>
                    <div
                        className="table-responsive"
                        style={{
                            overflowX: 'auto',
                            whiteSpace: 'nowrap',
                            maxHeight: "calc(100vh - 350px)",
                            overflowY: "auto",
                        }}
                    >
                        <table className="table table-hover mb-0" style={{
                            borderSpacing: '0.1rem',
                            borderCollapse: 'separate'
                        }}>
                            <thead style={{
                                position: "sticky",
                                top: 0,
                                background: "white",
                                zIndex: 1,
                            }}>
                                <tr>
                                    <th>#</th>
                                    <th>Batch ID</th>
                                    <th>Description</th>
                                    <th>Generated At</th>
                                    <th>Generated By</th>
                                    <th>Tag Count</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <AnimatePresence>
                                    {batches.map((batch, ind) => (
                                        <motion.tr
                                            key={batch?.batchId}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -20 }}
                                            transition={{ duration: 0.3 }}
                                        >
                                            <th scope="row">{ind + 1}</th>
                                            <td>
                                                <code className="text-primary">{batch?.batchId?.substring(0, 8)}...</code>
                                            </td>
                                            <td>{batch?.description || '-'}</td>
                                            <td>{formatCreatedDate(batch?.generatedAt)}</td>
                                            <td>{batch?.generatedBy?.name || '-'}</td>
                                            <td>
                                                <span className="badge bg-info">{batch?.tagCount}</span>
                                            </td>
                                            <td>
                                                <Button
                                                    color="soft-primary"
                                                    size="sm"
                                                    className="me-2"
                                                    onClick={() => openExportModal(batch)}
                                                >
                                                    <FeatherIcon icon="download" size={16} />
                                                    {' '}Export
                                                </Button>
                                            </td>
                                        </motion.tr>
                                    ))}
                                </AnimatePresence>
                            </tbody>
                        </table>
                    </div>

                    {loading && (
                        <div className="text-center py-3">
                            <Spinner color="primary" />
                        </div>
                    )}

                    <div className="text-center mt-3">
                        {hasMore ? (
                            <Button
                                color="primary"
                                onClick={loadMoreHistory}
                                disabled={loading}
                            >
                                {loading ? 'Loading...' : 'Load More'}
                            </Button>
                        ) : (
                            batches.length > 0 && (
                                <div className="text-muted">No more batches to load</div>
                            )
                        )}
                    </div>

                    {batches.length === 0 && !loading && (
                        <div className="text-center py-5 text-muted">
                            {searchTerm
                                ? "No batches match your search"
                                : "No tag batches generated yet. Click 'Generate Tags' to create your first batch."}
                        </div>
                    )}
                </CardBody>
            </Card>

            {/* Generate Modal */}
            <Modal
                size="lg"
                isOpen={generateModal}
                toggle={resetGenerateModal}
                centered
            >
                <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.2 }}
                >
                    <div className="modal-header">
                        <h5 className="modal-title">
                            {generationResult ? 'Tags Generated Successfully' : 'Generate UUID Tags'}
                        </h5>
                        <button
                            onClick={resetGenerateModal}
                            type="button"
                            className="btn-close"
                            aria-label="Close"
                        ></button>
                    </div>
                    <div className="modal-body">
                        {!generationResult ? (
                            <>
                                <Row className="mb-4">
                                    <label className="col-sm-3 col-form-label">Number of Tags *</label>
                                    <Col sm={9}>
                                        <Input
                                            type="number"
                                            min="1"
                                            max="10000"
                                            value={tagCount}
                                            onChange={(e) => setTagCount(parseInt(e.target.value) || 0)}
                                            placeholder="Enter number of tags (1-10,000)"
                                        />
                                        <small className="text-muted">Maximum 10,000 tags per batch</small>
                                    </Col>
                                </Row>
                                <Row className="mb-4">
                                    <label className="col-sm-3 col-form-label">Description</label>
                                    <Col sm={9}>
                                        <Input
                                            type="textarea"
                                            rows="2"
                                            value={description}
                                            onChange={(e) => setDescription(e.target.value)}
                                            placeholder="Optional batch description (e.g., 'Q1 2026 - Vendor XYZ')"
                                        />
                                    </Col>
                                </Row>
                                <div className="bg-light p-3 rounded mb-3">
                                    <small className="text-muted d-block mb-2">URL Preview:</small>
                                    <code className="text-break">
                                        {PORTAL_URL}/{previewUuid}
                                    </code>
                                </div>
                            </>
                        ) : (
                            <div className="text-center py-3">
                                <div className="mb-3">
                                    <FeatherIcon icon="check-circle" size={48} className="text-success" />
                                </div>
                                <h5 className="mb-3">Successfully Generated {generationResult.totalCount} Tags</h5>
                                <p className="text-muted mb-4">
                                    Batch ID: <code>{generationResult.batchId}</code>
                                </p>
                                <div className="d-flex justify-content-center gap-2">
                                    <Button
                                        color="success"
                                        onClick={() => handleExportGenerated('xlsx')}
                                    >
                                        <FeatherIcon icon="download" size={16} className="me-2" />
                                        Download Excel
                                    </Button>
                                    <Button
                                        color="outline-success"
                                        onClick={() => handleExportGenerated('csv')}
                                    >
                                        <FeatherIcon icon="file-text" size={16} className="me-2" />
                                        Download CSV
                                    </Button>
                                </div>
                            </div>
                        )}

                        {apiError && (
                            <Alert color="danger" className="mt-3">
                                <FeatherIcon icon="alert-circle" className="me-2" size={16} />
                                {apiError}
                            </Alert>
                        )}
                    </div>
                    <div className="modal-footer">
                        <Button
                            color="outline-secondary"
                            onClick={resetGenerateModal}
                        >
                            {generationResult ? 'Close' : 'Cancel'}
                        </Button>
                        {!generationResult && (
                            <Button
                                color="success"
                                onClick={handleGenerateTags}
                                disabled={generating || tagCount < 1 || tagCount > 10000}
                            >
                                {generating ? (
                                    <>
                                        <Spinner size="sm" className="me-2" />
                                        Generating...
                                    </>
                                ) : (
                                    <>
                                        <FeatherIcon icon="zap" size={16} className="me-2" />
                                        Generate {tagCount} Tags
                                    </>
                                )}
                            </Button>
                        )}
                    </div>
                </motion.div>
            </Modal>

            {/* Export Modal */}
            <Modal
                isOpen={exportModal}
                toggle={() => setExportModal(false)}
                centered
            >
                <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.2 }}
                >
                    <div className="modal-header">
                        <h5 className="modal-title">Export Tags</h5>
                        <button
                            onClick={() => setExportModal(false)}
                            type="button"
                            className="btn-close"
                            aria-label="Close"
                        ></button>
                    </div>
                    <div className="modal-body">
                        <p className="mb-3">
                            Exporting <strong>{selectedBatch?.tagCount}</strong> tags from batch{' '}
                            <code>{selectedBatch?.batchId?.substring(0, 8)}...</code>
                        </p>

                        <div className="mb-3">
                            <label className="form-label">Select Format</label>
                            <div className="d-flex gap-3">
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="exportFormat"
                                        id="formatXlsx"
                                        checked={exportFormat === 'xlsx'}
                                        onChange={() => setExportFormat('xlsx')}
                                    />
                                    <label className="form-check-label" htmlFor="formatXlsx">
                                        Excel (.xlsx)
                                    </label>
                                </div>
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="exportFormat"
                                        id="formatCsv"
                                        checked={exportFormat === 'csv'}
                                        onChange={() => setExportFormat('csv')}
                                    />
                                    <label className="form-check-label" htmlFor="formatCsv">
                                        CSV (.csv)
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div className="bg-light p-3 rounded">
                            <small className="text-muted d-block mb-2">Export will include:</small>
                            <ul className="mb-0 small">
                                <li>UUID column</li>
                                <li>Full URL column</li>
                                <li>Generated date</li>
                            </ul>
                        </div>

                        {apiError && (
                            <Alert color="danger" className="mt-3">
                                <FeatherIcon icon="alert-circle" className="me-2" size={16} />
                                {apiError}
                            </Alert>
                        )}
                    </div>
                    <div className="modal-footer">
                        <Button
                            color="outline-secondary"
                            onClick={() => setExportModal(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            color="primary"
                            onClick={handleExport}
                            disabled={exporting}
                        >
                            {exporting ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    Exporting...
                                </>
                            ) : (
                                <>
                                    <FeatherIcon icon="download" size={16} className="me-2" />
                                    Download
                                </>
                            )}
                        </Button>
                    </div>
                </motion.div>
            </Modal>
        </div>
    );
};

export default TagGenerator;
